<template>
  <div class="vehicle-comparison-container">
    <!-- Comparison Header -->
    <div class="comparison-header mb-4">
      <h2 class="text-h5 font-weight-bold">
        <v-icon left color="orange">mdi-car-compare</v-icon>
        Vehicle Comparison
      </h2>
      <p class="text-subtitle-1 grey--text">Compare similar vehicles side by side</p>

      <!-- Mobile View Toggle (show on mobile portrait/landscape with 3+ columns) -->
      <div v-if="($vuetify.breakpoint.xs || ($vuetify.breakpoint.sm && $vuetify.breakpoint.height < 600)) && groupedVehicles.length > 2" class="mobile-view-toggle mt-2">
        <v-btn-toggle
          v-model="mobileViewMode"
          mandatory
          dense
          color="orange"
        >
          <v-btn small value="scroll">
            <v-icon small>mdi-gesture-swipe-horizontal</v-icon>
            <span class="ml-1">Scroll</span>
          </v-btn>
          <v-btn small value="grid">
            <v-icon small>mdi-grid</v-icon>
            <span class="ml-1">Grid</span>
          </v-btn>
        </v-btn-toggle>
      </div>
    </div>

    <!-- Mobile Horizontal Scroll View (Grid Mode - for 3+ columns on small screens) -->
    <div v-if="($vuetify.breakpoint.xs || ($vuetify.breakpoint.sm && $vuetify.breakpoint.height < 600)) && groupedVehicles.length > 2 && mobileViewMode === 'grid'" class="mobile-comparison-scroll">
      <div class="mobile-scroll-container">
        <div
          v-for="(columnGroup, columnIndex) in groupedVehicles"
          :key="columnGroup.comparison_column || columnIndex"
          class="mobile-comparison-column"
        >
          <!-- Column Header -->
          <div class="column-header mb-3">
            <v-chip
              :color="columnIndex === 0 ? 'primary' : 'orange'"
              text-color="white"
              small
            >
              <v-icon left small>{{ columnIndex === 0 ? 'mdi-star' : 'mdi-compare' }}</v-icon>
              {{ columnIndex === 0 ? 'Your Search' : 'Similar' }}
            </v-chip>
            <div class="text-subtitle-2 mt-1">
              {{ columnGroup.make }} {{ columnGroup.model }}
              <span v-if="columnGroup.count > 1" class="grey--text">
                ({{ columnGroup.count }} vehicles)
              </span>
            </div>
          </div>

          <!-- All Vehicles in this Column -->
          <div
            v-for="(vehicle, vehicleIndex) in columnGroup.allVehicles"
            :key="vehicle.id || vehicleIndex"
            class="mb-3"
          >
            <v-card
              class="mx-auto comparison-card mobile-card"
              style="border-radius: 0px;"
              elevation="4"
              :class="{
                'primary-vehicle': columnIndex === 0 && vehicleIndex === 0,
                'selected-vehicle': isVehicleSelected(vehicle)
              }"
            >
              <!-- Selection Checkbox -->
              <div class="selection-checkbox">
                <v-checkbox
                  :input-value="isVehicleSelected(vehicle)"
                  @change="toggleVehicleSelection(vehicle)"
                  color="orange"
                  hide-details
                  dense
                  class="ma-0 pa-0"
                >
                  <template v-slot:label>
                    <span class="text-caption">Select</span>
                  </template>
                </v-checkbox>
              </div>

              <!-- Simplified mobile vehicle card content -->
              <v-img
                height="180"
                :src="getVehicleImage(vehicle)"
                @error="handleImageError"
              ></v-img>

              <v-card-title class="text-h6 font-weight-bold pa-2">
                {{vehicle.is_external && vehicle.title ? vehicle.title : `${vehicle.year} ${vehicle.make} ${vehicle.model}`}}
                <v-chip
                  v-if="vehicle.is_external"
                  color="blue"
                  text-color="white"
                  x-small
                  class="ml-1"
                >
                  {{vehicle.site_name || vehicle.source}}
                </v-chip>
              </v-card-title>

              <v-card-text class="pa-2">
                <div class="text-h6 font-weight-bold orange--text">
                  {{ currencyFormat(vehicle.amount) }}
                </div>
                <div class="text-caption">
                  <v-icon small class="mr-1">mdi-speedometer</v-icon>
                  {{vehicle.mileage =='N/A'  ? 'N/A': numberFormat(vehicle.mileage)+ ' mi'}}
                </div>
              </v-card-text>

              <v-card-actions class="pa-2">
                <v-btn
                  v-if="!vehicle.is_external"
                  color="primary"
                  block
                  small
                  @click="viewVehicleDetails(vehicle)"
                >
                  <v-icon left small>mdi-eye</v-icon>
                  View
                </v-btn>
                <v-btn
                  v-else
                  color="orange"
                  block
                  small
                  dark
                  @click="openExternalLink(vehicle.source_url)"
                >
                  View on {{ vehicle.site_name || vehicle.source }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </div>
        </div>
      </div>
      <div class="text-center mt-2">
        <v-chip small color="orange">
          <v-icon left small>mdi-gesture-swipe-horizontal</v-icon>
          Grid Mode - Swipe horizontally
        </v-chip>
      </div>
    </div>

    <!-- Vertical Grid View (Scroll Mode - for larger screens or 2 columns or mobile scroll mode) -->
    <v-row
      v-if="!(($vuetify.breakpoint.xs || ($vuetify.breakpoint.sm && $vuetify.breakpoint.height < 600)) && groupedVehicles.length > 2 && mobileViewMode === 'grid')"
      :class="{ 'mobile-scroll-compact': ($vuetify.breakpoint.xs || ($vuetify.breakpoint.sm && $vuetify.breakpoint.height < 600)) && groupedVehicles.length > 2 && mobileViewMode === 'scroll' }"
    >
      <v-col
        v-for="(columnGroup, columnIndex) in groupedVehicles"
        :key="columnGroup.comparison_column || columnIndex"
        :cols="getColumnSize"
        sm="6"
        md="4"
        lg="4"
        class="comparison-column"
      >
        <!-- Column Header -->
        <div class="column-header mb-3">
          <v-chip
            :color="columnIndex === 0 ? 'primary' : 'orange'"
            text-color="white"
            small
          >
            <v-icon left small>{{ columnIndex === 0 ? 'mdi-star' : 'mdi-compare' }}</v-icon>
            {{ columnIndex === 0 ? 'Your Search' : 'Similar' }}
          </v-chip>
          <div class="text-subtitle-2 mt-1">
            {{ columnGroup.make }} {{ columnGroup.model }}
          </div>
        </div>

        <!-- All Vehicles in this Column -->
        <div
          v-for="(vehicle, vehicleIndex) in columnGroup.allVehicles"
          :key="vehicle.id || vehicleIndex"
          class="mb-3"
        >
          <v-card
            class="mx-auto comparison-card"
            max-width="374"
            style="border-radius: 0px;"
            elevation="4"
            :class="{
              'primary-vehicle': columnIndex === 0 && vehicleIndex === 0,
              'selected-vehicle': isVehicleSelected(vehicle)
            }"
          >
            <!-- Selection Checkbox -->
            <div class="selection-checkbox">
              <v-checkbox
                :input-value="isVehicleSelected(vehicle)"
                @change="toggleVehicleSelection(vehicle)"
                color="orange"
                hide-details
                dense
                class="ma-0 pa-0"
              >
                <template v-slot:label>
                  <span class="text-caption">Select for Comparison</span>
                </template>
              </v-checkbox>
            </div>

            <!-- Vehicle Image Carousel (same as normal cards) -->
            <v-carousel v-model="vehicle.carouselModel" hide-delimiters height="250">
              <v-carousel-item
                v-for="(image, i) in (vehicle.images || [{ image: getVehicleImage(vehicle) }])"
                :key="i"
              >
                <v-img
                  height="250"
                  :src="image.image || getVehicleImage(vehicle)"
                  @error="handleImageError"
                ></v-img>
                <v-overlay absolute class="inventory-overlay" style="opacity: 0.4!important;">
                  <v-card color="transparent" class="d-flex justify-end align-end" style="height:100%;width:100%;text-align:right">
                    <v-card-text>{{i+1}}/{{(vehicle.images || []).length || 1}}</v-card-text>
                  </v-card>
                </v-overlay>
              </v-carousel-item>
            </v-carousel>

            <!-- Vehicle Title (same as normal cards) -->
            <v-card-title class="text-h4 font-weight-bold">
              {{vehicle.is_external && vehicle.title ? vehicle.title : `${vehicle.year} ${vehicle.make} ${vehicle.model}`}}
              <v-chip
                v-if="vehicle.is_external"
                color="blue"
                text-color="white"
                small
                class="ml-2"
              >
                {{vehicle.site_name || vehicle.source}}
              </v-chip>
            </v-card-title>

            <!-- AI Badge for external vehicles (same as normal cards) -->
            <div v-if="vehicle.is_external" class="ai-badge">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    color="orange"
                    text-color="white"
                    small
                    v-bind="attrs"
                    v-on="on"
                    class="ai-badge-chip"
                  >
                    <v-icon left small>mdi-robot</v-icon>
                    Found by SyncGuideX
                  </v-chip>
                </template>
                <span>This vehicle was found by SyncGuideX's search from {{vehicle.site_name || vehicle.source}}. Click "View on {{vehicle.site_name || vehicle.source}}" to see full details.</span>
              </v-tooltip>
            </div>

            <!-- Price Section (same as normal cards) -->
            <v-card-text class="pa-3">
              <v-row class="mx-0 mb-1">
                <v-col cols="12" class="pa-0">
                  <div class="text-h3 font-weight-bold text-success">
                    {{ currencyFormat(vehicle.amount) }}
                  </div>
                </v-col>
              </v-row>

              <!-- Vehicle Details Section (same as normal cards) -->
              <v-row class="mx-0 mb-1 mt-2">
                <div class="vehicle-overview__specs" style="width: 100%;">
                  <div class="vehicle-overview__mileage" style="float: left;">
                    <div class="vehicle-mileage" style="font-size: 1.3em;">
                      <template v-if="!vehicle.is_external">86,801 mi</template>
                      <template v-else>
                        <v-icon small class="mr-1">mdi-speedometer</v-icon>
                        {{vehicle.mileage =='N/A'  ? 'N/A': numberFormat(vehicle.mileage)+ ' mi'}}
                      </template>
                    </div>
                  </div>
                  <div class="vehicle-overview__colors" style="float: right;">
                    <div class="vehicle-colors">
                      <div class="vehicle-colors__ext">
                        <span class="vehicle-colors__label">Ext:</span>
                        <span class="vehicle-colors__value">{{ vehicle.exterior_color || 'N/A' }}</span>
                      </div>
                      <div class="vehicle-colors__int">
                        <span class="vehicle-colors__label">Int:</span>
                        <span class="vehicle-colors__value">{{ vehicle.interior_color || 'N/A' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </v-row>

              <!-- Location and Dealer Info (same as normal cards) -->
              <v-row class="mx-0 mb-1">
                <v-col cols="12" class="pa-0">
                  <div v-if="vehicle.description" class="vehicle-description mb-2">
                    {{ vehicle.description }}
                  </div>

                  <div v-if="vehicle.location" class="vehicle-location mb-1">
                    <v-icon small class="mr-1">mdi-map-marker</v-icon>
                    {{ vehicle.location }}
                  </div>

                  <div v-if="vehicle.dealer_name" class="vehicle-dealer">
                    <v-icon small class="mr-1">mdi-store</v-icon>
                    {{ vehicle.dealer_name }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>

            <!-- Action Buttons (same as normal cards) -->
            <v-card-actions class="pa-3">
              <v-btn
                v-if="!vehicle.is_external"
                color="primary"
                block
                @click="viewVehicleDetails(vehicle)"
              >
                View Details
              </v-btn>
              <v-btn
                v-else
                color="orange"
                block
                dark
                @click="openExternalLink(vehicle.source_url)"
              >
                View on {{ vehicle.site_name || vehicle.source }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </v-col>
    </v-row>

    <!-- Debug indicator for scroll mode -->
    <div v-if="($vuetify.breakpoint.xs || ($vuetify.breakpoint.sm && $vuetify.breakpoint.height < 600)) && groupedVehicles.length > 2 && mobileViewMode === 'scroll'" class="text-center mt-2">
      <v-chip small color="blue">
        <v-icon left small>mdi-view-grid</v-icon>
        Scroll Mode - Vertical grid
      </v-chip>
    </div>

    <!-- Comparison Summary -->
    <div v-if="groupedVehicles.length > 1" class="comparison-summary mt-4">
      <v-card outlined>
        <v-card-title class="text-h6">
          <v-icon left>mdi-chart-bar</v-icon>
          Quick Comparison
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <div class="summary-item">
                <div class="summary-label">Price Range</div>
                <div class="summary-value">
                  {{ currencyFormat(minPrice) }} - {{ currencyFormat(maxPrice) }}
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="summary-item">
                <div class="summary-label">Mileage Range</div>
                <div class="summary-value">
                  {{ formatMileage(minMileage) }} - {{ formatMileage(maxMileage) }}
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="summary-item">
                <div class="summary-label">Vehicle Types</div>
                <div class="summary-value">
                  {{ groupedVehicles.length }} different models
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>

    <!-- Selection Summary and Detailed Compare Button -->
    <div v-if="selectedVehicles.length > 0 && !showVehicleDetailModal && !showDetailedComparisonModal" class="selection-summary">
      <v-card outlined class="pa-3">
        <div class="d-flex align-center justify-space-between">
          <div class="selection-info">
            <v-icon left color="orange">mdi-checkbox-marked-circle</v-icon>
            <span class="font-weight-medium">
              {{ selectedVehicles.length }} vehicle{{ selectedVehicles.length > 1 ? 's' : '' }} selected
            </span>
            <span v-if="selectedVehicles.length < 2" class="text-caption grey--text ml-2">
              (Select {{ 2 - selectedVehicles.length }} more for detailed comparison)
            </span>
          </div>

          <div class="selection-actions">
            <v-btn
              text
              small
              color="grey"
              @click="clearSelections"
              class="mr-2"
            >
              <v-icon left small>mdi-close</v-icon>
              Clear
            </v-btn>

            <v-btn
              color="orange"
              dark
              :disabled="!canShowDetailedComparison"
              :loading="loadingDetailedComparison"
              @click="openDetailedComparison"
              class="detailed-compare-btn"
            >
              Detailed Compare
            </v-btn>
          </div>
        </div>

        <!-- Selected vehicles preview -->
        <div v-if="selectedVehicles.length > 0" class="selected-vehicles-preview mt-3">
          <div class="d-flex flex-wrap">
            <v-chip
              v-for="vehicle in selectedVehicles"
              :key="getVehicleId(vehicle)"
              small
              close
              color="orange"
              text-color="white"
              class="ma-1"
              @click:close="toggleVehicleSelection(vehicle)"
            >
              <v-icon left small>mdi-car</v-icon>
              {{ vehicle.is_external && vehicle.title ? vehicle.title : `${vehicle.year} ${vehicle.make} ${vehicle.model}` }}
            </v-chip>
          </div>
        </div>
      </v-card>
    </div>

    <!-- Detailed Comparison Modal -->
    <vehicle-detailed-comparison-modal
      v-model="showDetailedComparisonModal"
      :vehicles="selectedVehicles"
      :comparison-data="detailedComparisonData"
      @close="closeDetailedComparison"
      @clear-data="clearDetailedComparisonData"
      @vehicle-detail-opened="showVehicleDetailModal = true"
      @vehicle-detail-closed="showVehicleDetailModal = false"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import VehicleDetailedComparisonModal from './VehicleDetailedComparisonModal.vue';

export default {
  name: 'VehicleComparisonCard',
  components: {
    VehicleDetailedComparisonModal
  },
  props: {
    vehicles: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      mobileViewMode: 'grid', // Default to grid view on mobile (horizontal scroll)
      selectedVehicles: [], // Array to store selected vehicles for detailed comparison
      showDetailedComparisonModal: false, // Control detailed comparison modal
      detailedComparisonData: null, // Store AI-enhanced comparison data
      loadingDetailedComparison: false, // Loading state for AI processing
      showVehicleDetailModal: false // Track when vehicle detail modal is open
    };
  },
  computed: {
    groupedVehicles() {
      if (!this.vehicles.length) return [];

      console.log('🔍 VehicleComparisonCard received vehicles:', this.vehicles.length);

      // Group vehicles by comparison_column (API call source)
      const columnGroups = {};

      this.vehicles.forEach(vehicle => {
        // Use comparison_column if available, otherwise create a fallback column
        const columnKey = vehicle.comparison_column !== undefined ?
          vehicle.comparison_column :
          0; // Default to column 0 if no comparison_column

        if (!columnGroups[columnKey]) {
          columnGroups[columnKey] = [];
        }
        columnGroups[columnKey].push(vehicle);
      });

      console.log('🔍 Column groups:', Object.keys(columnGroups).map(key => ({
        column: key,
        count: columnGroups[key].length,
        makeModel: columnGroups[key][0] ? `${columnGroups[key][0].make} ${columnGroups[key][0].model}` : 'Unknown'
      })));

      // Create column objects for display
      const columns = [];

      // Sort column keys to maintain order (0, 1, 2)
      const sortedColumnKeys = Object.keys(columnGroups).sort((a, b) => parseInt(a) - parseInt(b));

      sortedColumnKeys.forEach((columnKey, index) => {
        const vehiclesInColumn = columnGroups[columnKey];

        if (vehiclesInColumn.length === 0) return;

        // Sort vehicles in this column by price
        vehiclesInColumn.sort((a, b) => {
          const priceA = a.amount || 0;
          const priceB = b.amount || 0;
          if (priceA === 0 && priceB === 0) return 0;
          if (priceA === 0) return 1;
          if (priceB === 0) return -1;
          return priceA - priceB;
        });

        // Get representative vehicle (first one after sorting)
        const representative = vehiclesInColumn[0];
        const prices = vehiclesInColumn.map(v => v.amount || 0).filter(p => p > 0);
        const minPrice = prices.length ? Math.min(...prices) : 0;
        const maxPrice = prices.length ? Math.max(...prices) : 0;

        // Create column object
        columns.push({
          ...representative,
          count: vehiclesInColumn.length,
          allVehicles: vehiclesInColumn,
          minPrice: minPrice,
          maxPrice: maxPrice,
          priceRange: vehiclesInColumn.length > 1 && minPrice !== maxPrice && minPrice > 0 ?
            `${this.currencyFormat(minPrice)} - ${this.currencyFormat(maxPrice)}` : null,
          comparison_column: columnKey,
          column_index: index,
          // Use the make/model from the representative vehicle or comparison data
          make: representative.comparison_make || representative.make || 'Unknown',
          model: representative.comparison_model || representative.model || 'Unknown'
        });
      });

      console.log('🔍 Final columns for display:', columns.map(col => ({
        column: col.comparison_column,
        makeModel: `${col.make} ${col.model}`,
        count: col.count
      })));

      // Limit to 3 columns for comparison
      return columns.slice(0, 3);
    },

    minPrice() {
      if (!this.groupedVehicles.length) return 0;
      // Get all vehicles from all columns
      const allVehicles = this.groupedVehicles.flatMap(group => group.allVehicles || [group]);
      const prices = allVehicles.map(v => v.amount || 0).filter(p => p > 0);
      return prices.length ? Math.min(...prices) : 0;
    },

    maxPrice() {
      if (!this.groupedVehicles.length) return 0;
      // Get all vehicles from all columns
      const allVehicles = this.groupedVehicles.flatMap(group => group.allVehicles || [group]);
      const prices = allVehicles.map(v => v.amount || 0).filter(p => p > 0);
      return prices.length ? Math.max(...prices) : 0;
    },

    minMileage() {
      if (!this.groupedVehicles.length) return 0;
      // Get all vehicles from all columns
      const allVehicles = this.groupedVehicles.flatMap(group => group.allVehicles || [group]);
      const mileages = allVehicles.map(v => this.parseMileage(v.mileage)).filter(m => m > 0);
      console.log('🔍 All vehicles for mileage calculation:', allVehicles.length);
      console.log('🔍 Parsed mileages:', mileages);
      return mileages.length ? Math.min(...mileages) : 0;
    },

    maxMileage() {
      if (!this.groupedVehicles.length) return 0;
      // Get all vehicles from all columns
      const allVehicles = this.groupedVehicles.flatMap(group => group.allVehicles || [group]);
      const mileages = allVehicles.map(v => this.parseMileage(v.mileage)).filter(m => m > 0);
      return mileages.length ? Math.max(...mileages) : 0;
    },

    uniqueMakes() {
      const makes = [...new Set(this.groupedVehicles.map(v => v.make).filter(Boolean))];
      return makes;
    },

    // Check if detailed compare button should be enabled
    canShowDetailedComparison() {
      return this.selectedVehicles.length >= 2 && this.selectedVehicles.length <= 3;
    },

    // Get appropriate column size based on view mode and screen size
    getColumnSize() {
      const isMobile = this.$vuetify.breakpoint.xs || (this.$vuetify.breakpoint.sm && this.$vuetify.breakpoint.height < 600);

      if (isMobile && this.groupedVehicles.length > 2) {
        // In mobile scroll mode, use larger columns for better readability
        if (this.mobileViewMode === 'scroll') {
          return this.groupedVehicles.length === 3 ? '6' : '12'; // 2 per row for 3 vehicles, 1 per row for more
        }
      }

      // Default sizing for non-mobile or grid mode
      return this.groupedVehicles.length > 2 ? '4' : '6';
    },

    // Get all vehicles from all columns for selection
    allVehicles() {
      return this.groupedVehicles.flatMap(group => group.allVehicles || [group]);
    }
  },
  methods: {
    getVehicleImage(vehicle) {
      if (vehicle.images && vehicle.images.length > 0) {
        return vehicle.images[0].image || require('@/assets/img/coming-soon.png');
      }
      return require('@/assets/img/coming-soon.png');
    },

    getLocation(vehicle) {
      if (vehicle.location) return vehicle.location;
      if (vehicle.city && vehicle.state) return `${vehicle.city}, ${vehicle.state}`;
      if (vehicle.dealer_name) return vehicle.dealer_name;
      return 'N/A';
    },

    formatMileage(mileage) {
      if (!mileage || mileage === 'N/A') return 'N/A';
      const parsed = this.parseMileage(mileage);
      if (parsed === 0) return 'N/A';
      return new Intl.NumberFormat('en-US').format(parsed) + ' mi';
    },

    parseMileage(mileage) {
      if (!mileage || mileage === 'N/A' || mileage === 'Unknown') return 0;
      if (typeof mileage === 'number') return mileage;

      // Convert to string and handle different formats
      const mileageStr = mileage.toString().toLowerCase();

      // Remove common text and keep only numbers and commas
      const cleanedMileage = mileageStr
        .replace(/[^\d,]/g, '') // Keep only digits and commas
        .replace(/,/g, ''); // Remove commas

      const parsed = parseInt(cleanedMileage);
      console.log(`🔍 Parsing mileage: "${mileage}" -> "${cleanedMileage}" -> ${parsed}`);

      return isNaN(parsed) ? 0 : parsed;
    },

    currencyFormat(value) {
      if (!value) return '$0';
      const roundedValue = Math.round(value);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(roundedValue);
    },

    viewVehicleDetails(vehicle) {
      // Emit vehicle tracking event to parent
      this.$emit('vehicle-viewed', {
        vehicle_id: vehicle.id,
        vehicle_data: {
          year: vehicle.year,
          make: vehicle.make,
          model: vehicle.model,
          price: vehicle.amount,
          mileage: vehicle.mileage || vehicle.odometer,
          vin: vehicle.vin,
          stock_number: vehicle.stock_number,
          is_external: vehicle.is_external,
          source: vehicle.source,
          site_name: vehicle.site_name
        }
      });

      if (vehicle.is_external) {
        // Open external link
        if (vehicle.source_url) {
          window.open(vehicle.source_url, '_blank');
        }
      } else {
        // Navigate to internal vehicle details
        this.$router.push(`/view-inventory-details/${vehicle.id}`);
      }
    },

    // Open external vehicle listing in a new tab (same as normal cards)
    openExternalLink(url) {
      if (!url) {
        return;
      }
      window.open(url, '_blank');
    },

    // Handle image loading errors (same as normal cards)
    handleImageError(event) {
      event.target.src = require('@/assets/img/coming-soon.jpg');
    },

    // Number formatting (same as normal cards)
    numberFormat(value) {
      if (!value) return 'N/A';
      return new Intl.NumberFormat().format(value);
    },

    // Vehicle selection methods
    isVehicleSelected(vehicle) {
      return this.selectedVehicles.some(selected => this.getVehicleId(selected) === this.getVehicleId(vehicle));
    },

    getVehicleId(vehicle) {
      return vehicle.id || `${vehicle.make}-${vehicle.model}-${vehicle.year}-${vehicle.amount}`;
    },

    toggleVehicleSelection(vehicle) {
      const vehicleId = this.getVehicleId(vehicle);
      const isSelected = this.isVehicleSelected(vehicle);

      if (isSelected) {
        // Remove from selection
        this.selectedVehicles = this.selectedVehicles.filter(selected =>
          this.getVehicleId(selected) !== vehicleId
        );
      } else {
        // Add to selection if under limit
        if (this.selectedVehicles.length < 3) {
          this.selectedVehicles.push(vehicle);
        } else {
          // Show warning for max selection
          Vue.$toast.warning('You can select maximum 3 vehicles for detailed comparison', {
            position: 'top-right'
          });
        }
      }
    },

    // Open detailed comparison modal
    async openDetailedComparison() {
      if (!this.canShowDetailedComparison) return;

      this.loadingDetailedComparison = true;

      try {
        // Prepare vehicle data for AI processing
        const vehicleData = this.selectedVehicles.map(vehicle => ({
          id: this.getVehicleId(vehicle),
          name: vehicle.is_external && vehicle.title ? vehicle.title : `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
          make: vehicle.make,
          model: vehicle.model,
          year: vehicle.year,
          price: vehicle.amount,
          mileage: vehicle.mileage,
          exterior_color: vehicle.exterior_color,
          interior_color: vehicle.interior_color,
          location: vehicle.location,
          dealer_name: vehicle.dealer_name,
          is_external: vehicle.is_external,
          source: vehicle.source,
          images: vehicle.images
        }));

        // Call AI service for enhanced comparison data
        const response = await axios.post('/api/vehicles/detailed-comparison', {
          vehicles: vehicleData
        });

        if (response.data.flag === 1) {
          this.detailedComparisonData = response.data.comparison_data;
          this.showDetailedComparisonModal = true;
        } else {
          throw new Error(response.data.message || 'Failed to generate detailed comparison');
        }

      } catch (error) {
        console.error('Error generating detailed comparison:', error);
        Vue.$toast.error('Failed to generate detailed comparison. Please try again.', {
          position: 'top-right'
        });
      } finally {
        this.loadingDetailedComparison = false;
      }
    },

    // Close detailed comparison modal
    closeDetailedComparison() {
      this.showDetailedComparisonModal = false;
      this.detailedComparisonData = null;
    },

    // Clear detailed comparison data (called from modal)
    clearDetailedComparisonData() {
      this.detailedComparisonData = null;
    },

    // Clear all selections
    clearSelections() {
      this.selectedVehicles = [];
      this.detailedComparisonData = null; // Also clear comparison data when clearing selections
    }
  }
};
</script>

<style scoped>
/* Vehicle Comparison Container */
.vehicle-comparison-container {
  padding: 16px;
}

.comparison-header {
  text-align: center;
  margin-bottom: 24px;
}

.mobile-view-toggle {
  display: flex;
  justify-content: center;
}

.comparison-column {
  position: relative;
  max-height: 80vh;
  overflow-y: auto;
}

.column-header {
  text-align: center;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 16px;
}

.comparison-card {
  position: relative;
  transition: transform 0.2s ease-in-out;
  margin-bottom: 12px;
}

.comparison-card:hover {
  transform: translateY(-2px);
}

.primary-vehicle {
  border: 2px solid #1976d2;
}

.selected-vehicle {
  border: 2px solid #ff9800 !important;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3) !important;
}

/* Selection checkbox styling */
.selection-checkbox {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 3;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  padding: 2px 6px;
  backdrop-filter: blur(4px);
}

.selection-checkbox .v-input--checkbox {
  margin: 0;
  padding: 0;
}

.selection-checkbox .v-input--checkbox .v-input__control {
  min-height: auto;
}

/* Selection summary styling */
.selection-summary {
  position: sticky;
  bottom: 16px;
  z-index: 10;
  margin-top: 16px;
}

.selection-summary .v-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border: 1px solid #e0e0e0;
}

.detailed-compare-btn {
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.5px;
}

.detailed-compare-btn:not(.v-btn--disabled) {
  animation: pulse-orange 2s infinite;
}

@keyframes pulse-orange {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.4);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(255, 152, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 152, 0, 0);
  }
}

.selected-vehicles-preview .v-chip {
  transition: all 0.2s ease;
}

.selected-vehicles-preview .v-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Styles from normal vehicle cards */
.v-window__next .v-icon{
  font-size: 25px!important;
}
.v-window__prev .v-icon{
  font-size: 25px!important;
}
.inventory-overlay .v-overlay__content{
  height:100%;width:100%;
}

/* AI Badge Styling (same as normal cards) */
.ai-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
}

.ai-badge-chip {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  animation: pulse-light 2s infinite;
}

@keyframes pulse-light {
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }

  70% {
    box-shadow: 0 0 0 6px rgba(25, 118, 210, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
}

.v-card__title {
  word-break: normal !important;
}

.vehicle-description, .vehicle-location, .vehicle-dealer {
  width: 100%;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
  line-height: 1.4;
}

.vehicle-description {
  max-height: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
}

.vehicle-location, .vehicle-dealer {
  display: flex;
  align-items: center;
}

/* Vehicle specs styling (same as normal cards) */
.vehicle-overview__specs {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.vehicle-overview__mileage {
  flex: 1;
}

.vehicle-mileage {
  font-weight: 600;
  color: #333;
}

.vehicle-overview__colors {
  flex: 1;
  text-align: right;
}

.vehicle-colors {
  font-size: 0.875rem;
}

.vehicle-colors__ext, .vehicle-colors__int {
  margin-bottom: 2px;
}

.vehicle-colors__label {
  font-weight: 600;
  color: #666;
  margin-right: 4px;
}

.vehicle-colors__value {
  color: #333;
}

.comparison-summary {
  margin-top: 24px;
}

.summary-item {
  text-align: center;
}

.summary-label {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 4px;
}

.summary-value {
  font-weight: 600;
  font-size: 1rem;
}

/* Scrollbar styling for columns */
.comparison-column::-webkit-scrollbar {
  width: 6px;
}

.comparison-column::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.comparison-column::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.comparison-column::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Mobile Responsive Styles */
@media (max-width: 960px) {
  .comparison-column {
    margin-bottom: 16px;
    max-height: none;
    overflow-y: visible;
  }

  .column-header {
    position: relative;
  }

  .vehicle-comparison-container {
    padding: 8px;
  }

  .comparison-header {
    margin-bottom: 16px;
  }

  .comparison-header h2 {
    font-size: 1.3rem !important;
  }

  .comparison-header p {
    font-size: 0.9rem;
  }
}

/* Extra small screens (phones) */
@media (max-width: 600px) {
  .vehicle-comparison-container {
    padding: 4px;
  }

  .comparison-column {
    padding: 0 4px !important;
    margin-bottom: 12px;
  }

  .comparison-card {
    max-width: 100% !important;
    margin-bottom: 8px;
  }

  .column-header {
    margin-bottom: 8px;
    padding: 4px 0;
  }

  .column-header .v-chip {
    font-size: 0.75rem;
  }

  .column-header .text-subtitle-2 {
    font-size: 0.8rem;
  }

  /* Make vehicle cards more compact on mobile */
  .v-carousel {
    height: 200px !important;
  }

  .v-card-title {
    font-size: 1rem !important;
    padding: 8px 12px !important;
    line-height: 1.2;
  }

  .v-card-text {
    padding: 8px 12px !important;
  }

  .v-card-actions {
    padding: 8px 12px !important;
  }

  /* Grid mode specific styles for mobile - smaller text when 3+ columns */
  .mobile-grid-compact .comparison-column {
    .v-card-title {
      font-size: 0.75rem !important;
      padding: 4px 6px !important;
      line-height: 1.0;
    }

    .v-card-text {
      padding: 4px 6px !important;
      font-size: 0.75rem;
    }

    .v-card-actions {
      padding: 4px 6px !important;
    }

    .v-btn {
      font-size: 0.55rem !important;
      min-height: 20px !important;
      padding: 0 4px !important;
      min-width: 24px !important;
    }

    .column-header .v-chip {
      font-size: 0.55rem !important;
      height: 20px !important;
    }

    .column-header .text-subtitle-2 {
      font-size: 0.65rem !important;
    }

    .v-carousel {
      height: 160px !important;
    }

    /* Make vehicle details even more compact */
    .vehicle-overview__specs {
      font-size: 0.75rem;
    }

    .vehicle-mileage {
      font-size: 0.9rem !important;
    }

    .vehicle-colors__label,
    .vehicle-colors__value {
      font-size: 0.7rem;
    }

    /* Compact price display - make it more prominent */
    .text-h4 {
      font-size: 1rem !important;
      font-weight: bold !important;
    }

    /* Smaller chips for external vehicles */
    .v-chip {
      font-size: 0.55rem !important;
      height: 18px !important;
    }

    /* Compact location and dealer info */
    .text-caption {
      font-size: 0.7rem !important;
    }

    /* Make external vehicle source chips smaller */
    .v-card-title .v-chip {
      font-size: 0.5rem !important;
      height: 16px !important;
      padding: 0 4px !important;
    }

    /* Compact vehicle overview sections */
    .vehicle-overview__mileage,
    .vehicle-overview__colors {
      font-size: 0.7rem;
    }

    /* Shorter button text for mobile */
    .v-btn .v-icon + span {
      display: none;
    }

    .v-btn .v-icon {
      margin-right: 0 !important;
    }

    /* Make "VIEW ON" buttons more compact */
    .v-btn {
      letter-spacing: -0.5px;
      text-transform: none;
    }

    /* Specific styling for external buttons */
    .v-btn[href] {
      font-size: 0.5rem !important;
      min-height: 18px !important;
      padding: 0 3px !important;
    }
  }

  /* Adjust comparison summary for mobile */
  .comparison-summary .v-card-title {
    font-size: 1.1rem !important;
    padding: 12px !important;
  }

  .comparison-summary .v-card-text {
    padding: 0 12px 12px !important;
  }

  .summary-item {
    text-align: center;
    margin-bottom: 8px;
  }

  .summary-label {
    font-size: 0.8rem;
  }

  .summary-value {
    font-size: 0.9rem;
  }

  /* Mobile responsive styles for selection features */
  .selection-summary {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0;
    border-radius: 0;
    z-index: 1000;
  }

  .selection-summary .v-card {
    border-radius: 0;
    border-left: none;
    border-right: none;
    border-bottom: none;
  }

  .selection-info,
  .selection-actions {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .selection-actions {
    width: 100%;
    margin-top: 8px;
  }

  .selection-actions .v-btn {
    width: 100%;
    margin: 2px 0 !important;
  }

  .detailed-compare-btn {
    font-size: 0.9rem !important;
  }

  .selection-checkbox {
    top: 4px;
    left: 4px;
    padding: 1px 4px;
  }

  .selection-checkbox .text-caption {
    font-size: 0.7rem;
  }
}

/* Mobile Horizontal Scroll Styles */
.mobile-comparison-scroll {
  width: 100%;
  overflow: hidden;
}

.mobile-scroll-container {
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  gap: 12px;
  padding: 0 4px 16px 4px;
}

.mobile-comparison-column {
  flex: 0 0 280px;
  scroll-snap-align: start;
  max-width: 280px;
}

.mobile-card {
  width: 100% !important;
  max-width: 100% !important;
}

/* Custom scrollbar for mobile horizontal scroll */
.mobile-scroll-container::-webkit-scrollbar {
  height: 6px;
}

.mobile-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.mobile-scroll-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.mobile-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Landscape phones and small tablets */
@media (max-width: 768px) and (orientation: landscape) {
  .comparison-column {
    max-height: 70vh;
    overflow-y: auto;
  }

  .v-carousel {
    height: 180px !important;
  }

  .mobile-comparison-column {
    flex: 0 0 240px;
    max-width: 240px;
  }
}

/* Very small screens adjustments */
@media (max-width: 360px) {
  .mobile-comparison-column {
    flex: 0 0 260px;
    max-width: 260px;
  }

  .mobile-scroll-container {
    gap: 8px;
    padding: 0 2px 12px 2px;
  }

  /* Mobile scroll mode - more readable vertical grid */
  .mobile-scroll-compact .comparison-column {
    padding: 0 4px !important;

    .v-card-title {
      font-size: 0.9rem !important;
      padding: 6px 8px !important;
      line-height: 1.1;
    }

    .v-card-text {
      padding: 6px 8px !important;
      font-size: 0.8rem;
    }

    .v-card-actions {
      padding: 6px 8px !important;
    }

    .v-btn {
      font-size: 0.7rem !important;
      min-height: 24px !important;
      padding: 0 6px !important;
      min-width: 40px !important;
    }

    .column-header .v-chip {
      font-size: 0.6rem !important;
      height: 20px !important;
    }

    .column-header .text-subtitle-2 {
      font-size: 0.7rem !important;
    }

    .v-carousel {
      height: 180px !important;
    }

    /* Readable price display */
    .text-h4 {
      font-size: 1.2rem !important;
      font-weight: bold !important;
      color: #ff9800 !important;
    }

    /* Readable chips */
    .v-card-title .v-chip {
      font-size: 0.6rem !important;
      height: 18px !important;
      padding: 0 4px !important;
    }

    /* All chips readable */
    .v-chip {
      font-size: 0.6rem !important;
      height: 20px !important;
      padding: 0 4px !important;
    }

    /* Show button text in scroll mode */
    .v-btn span {
      display: inline;
    }

    /* Readable vehicle details */
    .vehicle-overview__specs,
    .text-caption {
      font-size: 0.75rem !important;
    }
  }
}
</style>
